// =============================================================================
// Server-safe system-primitive resolver
// =============================================================================
//
// Mirrors {@link ./resolveSystem}. Two ways themes participate:
//
//   1. Whole-page system override → lives in {@code theme.system}
//      (resolved by {@link ./resolveSystem}). When this exists for a
//      given route, it owns rendering top-to-bottom and the primitive
//      resolver is irrelevant for that page.
//
//   2. Primitive override → lives in {@code theme.primitives}. Used by
//      default system pages that didn't get a whole-page override. A
//      theme might ship {@code CourseCard} and {@code EventCard}
//      overrides without touching any whole pages — every default
//      system page that renders cards picks up the new look
//      automatically.
//
// Critical: every primitive ALWAYS has a default in {@link ./SystemUI}
// (for the small primitives that ship with the runtime) or in the
// blocks package (for sub-primitives like {@code BlogHero} and
// {@code WikiPageHeader}). {@link resolveSystemPrimitive} always
// returns a concrete component — primitives are guaranteed to render.
//
// To add a new primitive:
//   1. Add the prop type + key to {@link SystemPrimitives} in
//      {@link ./SystemPrimitives.types}.
//   2. Add a default implementation in {@link ./SystemUI} (or in
//      {@code @akademi/site-builder-blocks/src/system|blog/...} for
//      sub-primitives) and import it into the {@code DEFAULTS} map
//      below.
//   3. Re-export the prop type from this package's public barrel.

import type { SystemPrimitives, SystemPrimitiveKind } from './SystemPrimitives.types';
import {
  CourseCard as DefaultCourseCard,
  ArticleCard as DefaultArticleCard,
  EventCard as DefaultEventCard,
  CategoryCard as DefaultCategoryCard,
  PageHeader as DefaultPageHeader,
  Pill as DefaultPill,
  EmptyState as DefaultEmptyState,
} from './SystemUI';
import { resolveSystemPrimitive as registryResolveSystemPrimitive } from './themeRegistry';

// The defaults map — what every theme falls back to for the small
// primitives shipped in {@link ./SystemUI}. Sub-primitives that live
// in the blocks package (BlogHero, WikiPageHeader) are intentionally
// not listed here — their defaults are imported and substituted by
// the consuming layouts (which live in the blocks package too, so
// the import stays local).
//
// {@code satisfies Partial<...>} enforces type-correctness without
// forcing exhaustiveness over the whole primitive set.
const DEFAULTS = {
  CourseCard: DefaultCourseCard,
  ArticleCard: DefaultArticleCard,
  EventCard: DefaultEventCard,
  CategoryCard: DefaultCategoryCard,
  PageHeader: DefaultPageHeader,
  Pill: DefaultPill,
  EmptyState: DefaultEmptyState,
} satisfies Partial<{ [K in SystemPrimitiveKind]: NonNullable<SystemPrimitives[K]> }>;

/**
 * Resolve a primitive component for the active theme.
 *
 * Resolution order:
 *   1. theme.primitives[kind]
 *   2. themes['default'].primitives[kind]   (for site-wide custom defaults)
 *   3. {@link DEFAULTS}[kind]                (the shipped runtime fallback)
 *
 * For primitives in {@link DEFAULTS} this always returns a concrete
 * component. For primitives without a runtime default (BlogHero,
 * WikiPageHeader — defaults live in
 * {@code @akademi/site-builder-blocks}) callers must pass their own
 * fallback or use {@link tryResolveSystemPrimitive}.
 */
export function resolveSystemPrimitive<K extends SystemPrimitiveKind>(
  themeId: string | null | undefined,
  kind: K,
): NonNullable<SystemPrimitives[K]> | undefined {
  const themed = registryResolveSystemPrimitive(themeId, kind);
  if (themed) return themed;
  const shipped = (
    DEFAULTS as Partial<Record<SystemPrimitiveKind, NonNullable<SystemPrimitives[K]>>>
  )[kind];
  return shipped;
}

/**
 * Same resolution as {@link resolveSystemPrimitive} but returns
 * {@code null} when only the shipped fallback would apply (i.e. no
 * theme has registered an override for this kind).
 *
 * Used by layouts that need to decide between a theme override and a
 * per-tenant variant:
 *
 * {@code
 *   const themeArticleCard = tryResolveSystemPrimitive(themeId, 'ArticleCard');
 *   const ArticleCard = themeArticleCard ?? resolveArticleCardVariant(cfg.variant);
 * }
 *
 * Themes win over variants — the layered model is preserved.
 */
export function tryResolveSystemPrimitive<K extends SystemPrimitiveKind>(
  themeId: string | null | undefined,
  kind: K,
): NonNullable<SystemPrimitives[K]> | null {
  return registryResolveSystemPrimitive(themeId, kind) ?? null;
}
